import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { PageHeader } from '@/components/Shared';
import {
  BarChart3, FileSpreadsheet, FileText, Download, Printer, Boxes, ArrowLeftRight, ShoppingCart,
  Users2, UserCheck, Percent, DollarSign, TrendingUp, AlertTriangle, Receipt, Wallet, Calculator, ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';

const PRINTABLE = [
  { key: 'full_inventory', title: 'Full Inventory List', icon: ListChecks },
  { key: 'customer_balance', title: 'Customer Balance / Debtor Report', icon: Wallet },
  { key: 'employee_balance', title: 'Employee Balance Report', icon: UserCheck },
  { key: 'accounting_summary', title: 'Accounting Summary Report', icon: Calculator },
];
const OTHER = [
  { key: 'inventory', title: 'Inventory Report', icon: Boxes },
  { key: 'movement', title: 'Inventory Movement', icon: ArrowLeftRight },
  { key: 'purchases', title: 'Purchase Report', icon: ShoppingCart },
  { key: 'customer_sales', title: 'Customer Sales', icon: Users2 },
  { key: 'employee_sales', title: 'Employee Sales', icon: UserCheck },
  { key: 'commission', title: 'Employee Commission', icon: Percent },
  { key: 'revenue', title: 'Revenue Report', icon: DollarSign },
  { key: 'profit', title: 'Profit Report', icon: TrendingUp },
  { key: 'low_stock', title: 'Low Stock Report', icon: AlertTriangle },
  { key: 'invoice', title: 'Invoice Report', icon: Receipt },
  { key: 'buy_requests', title: 'Buy Requests', icon: ShoppingCart },
  { key: 'return_requests', title: 'Return Requests', icon: ArrowLeftRight },
  { key: 'return_credits', title: 'Return Credits', icon: Wallet },
  { key: 'payments', title: 'Payments Report', icon: DollarSign },
  { key: 'damaged_inventory', title: 'Damaged Inventory', icon: AlertTriangle },
  { key: 'scrap_inventory', title: 'Defective / Scrap Inventory', icon: Boxes },
];

const EMPTY_FILTERS = { start: '', end: '', customer_id: 'all', employee_id: 'all', category: 'all' };

function ReportCards({ items, onOpen }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((r) => (
        <Card key={r.key} className="p-5 cursor-pointer transition-shadow hover:shadow-lg" onClick={() => onOpen(r)} data-testid={`report-card-${r.key}`}>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"><r.icon className="h-5 w-5" /></span>
            <div><div className="font-heading font-semibold">{r.title}</div><div className="text-xs text-muted-foreground">View, filter, print &amp; export</div></div>
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function Reports() {
  const [active, setActive] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get('/customers').then(({ data }) => setCustomers(data)).catch(() => {});
    api.get('/users').then(({ data }) => setEmployees(data.filter((u) => u.role === 'employee'))).catch(() => {});
    api.get('/products/categories').then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const queryParams = useCallback(() => {
    const p = {};
    if (filters.start) p.start = filters.start;
    if (filters.end) p.end = filters.end;
    if (filters.customer_id !== 'all') p.customer_id = filters.customer_id;
    if (filters.employee_id !== 'all') p.employee_id = filters.employee_id;
    if (filters.category !== 'all') p.category = filters.category;
    return p;
  }, [filters]);

  const fetchReport = useCallback(async (rtype) => {
    setLoading(true); setReport(null);
    try { const { data } = await api.get(`/reports/${rtype}`, { params: queryParams() }); setReport(data); }
    catch { toast.error('Failed to load report'); } finally { setLoading(false); }
  }, [queryParams]);

  const openReport = (r) => { setActive(r); setFilters(EMPTY_FILTERS); };

  useEffect(() => { if (active) fetchReport(active.key); }, [active, fetchReport]);

  const exportReport = async (fmt) => {
    try {
      const res = await api.get(`/reports/${active.key}/export`, { params: { fmt, ...queryParams() }, responseType: 'blob' });
      const ext = fmt === 'excel' ? 'xlsx' : fmt;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `${active.key}.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const printReport = () => {
    document.body.classList.add('printing-report');
    const cleanup = () => { document.body.classList.remove('printing-report'); window.removeEventListener('afterprint', cleanup); };
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Generate, filter, print and export professional reports" testid="reports-header" />

      <h3 className="font-heading font-semibold mb-3">Printable Reports</h3>
      <ReportCards items={PRINTABLE} onOpen={openReport} />
      <h3 className="font-heading font-semibold mb-3 mt-8">All Reports</h3>
      <ReportCards items={OTHER} onOpen={openReport} />

      <Sheet open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setReport(null); } }}>
        <SheetContent className="w-full sm:max-w-4xl overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">{active?.title}</SheetTitle></SheetHeader>

          {/* Filters */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            <div><Label className="text-xs">From</Label><Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} data-testid="report-filter-start" className="mt-1" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} data-testid="report-filter-end" className="mt-1" /></div>
            <div><Label className="text-xs">Customer</Label>
              <Select value={filters.customer_id} onValueChange={(v) => setFilters({ ...filters, customer_id: v })}>
                <SelectTrigger className="mt-1" data-testid="report-filter-customer"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Customers</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Employee</Label>
              <Select value={filters.employee_id} onValueChange={(v) => setFilters({ ...filters, employee_id: v })}>
                <SelectTrigger className="mt-1" data-testid="report-filter-employee"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Employees</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Category</Label>
              <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                <SelectTrigger className="mt-1" data-testid="report-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Categories</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end"><Button variant="secondary" className="w-full" onClick={() => setFilters(EMPTY_FILTERS)} data-testid="report-filter-reset">Reset</Button></div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button size="sm" onClick={printReport} data-testid="reports-print-button"><Printer className="h-4 w-4 mr-1" /> Print</Button>
            <Button variant="outline" size="sm" onClick={() => exportReport('pdf')} data-testid="reports-export-pdf-button"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
            <Button variant="outline" size="sm" onClick={() => exportReport('excel')} data-testid="reports-export-excel-button"><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
            <Button variant="outline" size="sm" onClick={() => exportReport('csv')} data-testid="reports-export-csv-button"><Download className="h-4 w-4 mr-1" /> CSV</Button>
          </div>

          <div className="mt-4 overflow-x-auto">
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {report && (
              <Table>
                <TableHeader><TableRow>{report.columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {report.rows.length === 0 ? <TableRow><TableCell colSpan={report.columns.length} className="text-center text-muted-foreground py-8">No data available</TableCell></TableRow> :
                    report.rows.map((row, i) => <TableRow key={`${row[report.columns[0]?.key] ?? 'row'}-${i}`}>{report.columns.map((c) => <TableCell key={c.key} className="text-sm">{String(row[c.key] ?? '')}</TableCell>)}</TableRow>)}
                </TableBody>
              </Table>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Hidden printable layout */}
      {report && active && (
        <div className="report-print-area hidden" aria-hidden="true">
          <div style={{ borderBottom: '2px solid #123A63', paddingBottom: 8, marginBottom: 16 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#123A63' }}>Lailany Inc</div>
            <div style={{ fontSize: 12 }}>351 N Walnut Rd Ste 3, Turlock, CA 95382</div>
            <div style={{ fontSize: 12 }}>Phone: 209-559-4600</div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{report.title}</div>
            <div style={{ fontSize: 11, color: '#555', textAlign: 'right' }}>
              <div>Generated: {new Date(report.generated_at).toLocaleString()}</div>
              {report.filter_summary && <div>{report.filter_summary}</div>}
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>{report.columns.map((c) => <th key={c.key} style={{ background: '#123A63', color: '#fff', textAlign: 'left', padding: '6px 8px', border: '1px solid #ccc' }}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {report.rows.map((row, i) => (
                <tr key={`p-${row[report.columns[0]?.key] ?? 'row'}-${i}`} style={{ background: i % 2 ? '#f3f4f6' : '#fff' }}>
                  {report.columns.map((c) => <td key={c.key} style={{ padding: '5px 8px', border: '1px solid #ddd' }}>{String(row[c.key] ?? '')}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
